export interface ResultValue {
	value: any;
	data?: string;
	settings?: {
		unit?: string | null;
	};
}

export const parseJson = (value: string) => {
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

export const isImage = (mimeType?: string) => mimeType?.startsWith('image/');
export const isAudio = (mimeType?: string) => mimeType?.startsWith('audio/');

export const isImageUrl = (value?: string) =>
	Boolean(value?.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i));

export const getMimeTypeFromBytes = (bytes: Uint8Array): string => {
	const header = Array.from(bytes.slice(0, 16))
		.map(byte => String.fromCharCode(byte))
		.join('');
	// WAV
	if (header.startsWith('RIFF') && header.includes('WAVE')) {
		return 'audio/wav';
	}
	// MP3
	if (
		(bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) ||
		header.startsWith('ID3')
	) {
		return 'audio/mpeg';
	}
	// M4A / AAC in MP4 container
	if (
		header.includes('ftypM4A') ||
		header.includes('ftypisom') ||
		header.includes('ftypmp42') ||
		header.includes('ftypMSNV') ||
		header.includes('ftyp3gp')
	) {
		return 'audio/mp4';
	}
	// OGG
	if (header.startsWith('OggS')) {
		return 'audio/ogg';
	}
	// FLAC
	if (header.startsWith('fLaC')) {
		return 'audio/flac';
	}
	// JPEG
	if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
		return 'image/jpeg';
	}
	// PNG
	if (
		bytes[0] === 0x89 &&
		bytes[1] === 0x50 &&
		bytes[2] === 0x4e &&
		bytes[3] === 0x47
	) {
		return 'image/png';
	}
	// GIF
	if (header.startsWith('GIF')) {
		return 'image/gif';
	}
	// WebP
	if (header.includes('WEBP')) {
		return 'image/webp';
	}
	return '';
};

export const binaryStringToUint8Array = (value: string): Uint8Array => {
	const bytes = new Uint8Array(value.length);

	for (let i = 0; i < value.length; i++) {
		bytes[i] = value.charCodeAt(i) & 0xff;
	}

	return bytes;
};

export const responseDataToBlob = async (data: any): Promise<Blob> => {
	if (data instanceof Blob) {
		const buffer = await data.arrayBuffer();
		const bytes = new Uint8Array(buffer);
		const mimeType = data.type || getMimeTypeFromBytes(bytes);

		return new Blob([buffer], { type: mimeType });
	}

	if (data instanceof ArrayBuffer) {
		const bytes = new Uint8Array(data);
		const mimeType = getMimeTypeFromBytes(bytes);

		return new Blob([data], { type: mimeType });
	}

	if (typeof data === 'string') {
		const bytes = binaryStringToUint8Array(data);
		const mimeType = getMimeTypeFromBytes(bytes);

		return new Blob([bytes], { type: mimeType });
	}

	const blob = new Blob([data]);
	const buffer = await blob.arrayBuffer();
	const bytes = new Uint8Array(buffer);
	const mimeType = getMimeTypeFromBytes(bytes);

	return new Blob([buffer], { type: mimeType });
};
